/**
 * Copyright 2025 (c) Karsten Röthig, FVA GmbH (for umati and VDW e.V.)
 */
import _ from 'lodash';
import { Specifications } from 'Specifications';
import UnknownTypeTemplateJson from '../templates/pubSub/_UnknownType.json';
import {
  Section,
  SectionRow,
  SectionRowElementBadge,
  Template,
  Timeline,
  UaVariable,
  UaVariableEnum,
} from '../templates/pubSub/types/TemplateType';
import {
  FilterBrowsename,
  FilterCurrentNode,
  FilterReferenceAndTypeId,
  PubSubNodeFilterFactory,
} from './PubSub/NodeFilters';
import { NodeId } from './PubSub/PubSubTypes';

interface TypeTemplateIndex {
  [key: string]: Template;
}

const typeTemplateIndex: TypeTemplateIndex = Specifications.flatMap((spec) => spec.TypeDefinitions)
  .filter((typeDef) => typeDef.TemplatePubSub)
  .reduce((map, typeDef) => {
    map[typeDef.Type] = typeDef.TemplatePubSub;
    return map;
  }, {});

const DEFAULT_VALUE_UNDEFINED = 'Undefined';
const DEFAULT_BADGE_BACKGROUND_COLOR = '#343a40';
const DEFAULT_BADGE_TEXT_COLOR = '#FFFFFF';

interface OptionalContainerContext {
  id: string;
  optional?: boolean;
  validationNodePath?: string;
  ValidationNode?: string;
}

export default class TemplateHelperPubSub {
  private static resolveValidationNodePath = (container?: OptionalContainerContext): string | undefined => {
    if (!container) {
      return undefined;
    }

    // Keep support for legacy template key while preferring the explicit path name.
    return container.validationNodePath || container.ValidationNode;
  };

  private static shouldHideOptionalContainer = (
    validationNode: any,
    containerType: 'section' | 'row',
    container?: OptionalContainerContext,
    context?: string,
  ): boolean => {
    if (!container || container.optional !== true) {
      return false;
    }

    const validationNodePath = this.resolveValidationNodePath(container);
    if (!validationNodePath || validationNode !== undefined) {
      return false;
    }

    console.warn(
      `[TemplateHelperPubSub] Optional ${containerType} hidden because validation node could not be resolved.`,
      {
        context,
        id: container.id,
        validationNodePath,
        optional: container.optional === true,
      },
    );

    return true;
  };

  static resolveSectionValidationNodePath = (section?: Section): string | undefined => {
    return this.resolveValidationNodePath(section);
  };

  static shouldHideOptionalSection = (validationNode: any, section?: Section, context?: string): boolean => {
    return this.shouldHideOptionalContainer(validationNode, 'section', section, context);
  };

  static resolveRowValidationNodePath = (row?: SectionRow): string | undefined => {
    return this.resolveValidationNodePath(row);
  };

  static shouldHideOptionalRow = (validationNode: any, row?: SectionRow, context?: string): boolean => {
    return this.shouldHideOptionalContainer(validationNode, 'row', row, context);
  };

  static isVariableUnresolved = (value: any, variable?: UaVariable): boolean => {
    if (!variable) {
      return false;
    }

    const resolvedValue = this.resolveVariableValue(value, variable);
    return value === undefined || resolvedValue === undefined;
  };

  static logUnresolvedVariable = (value: any, variable?: UaVariable, context?: string): boolean => {
    const isUnresolved = this.isVariableUnresolved(value, variable);
    if (!isUnresolved || !variable) {
      return false;
    }

    console.warn('[TemplateHelperPubSub] Variable could not be resolved.', {
      context,
      variablePath: variable.variablePath,
      dataType: variable.dataType,
      optional: variable.optional === true,
      value,
    });

    return true;
  };

  static shouldHideOptionalVariable = (value: any, variable?: UaVariable, context?: string): boolean => {
    const isUnresolved = this.logUnresolvedVariable(value, variable, context);
    return variable?.optional === true && isUnresolved;
  };

  static resolveFieldValue = (value: any, field?: string): any => {
    if (!field) {
      return value;
    }

    return field.split('.').reduce((currentValue, key) => currentValue?.[key], value);
  };

  static resolveVariableValue = (value: any, variable?: UaVariable): any => {
    if (value === null || value === undefined) {
      return value;
    }

    let resolvedValue = value;
    if (variable?.property) {
      resolvedValue = _.get(resolvedValue, variable.property, value);
    }

    if (variable?.dataType === 'Structure') {
      if (variable.field) {
        resolvedValue = this.resolveFieldValue(resolvedValue, variable.field);
      } else {
        resolvedValue = JSON.stringify(resolvedValue);
      }
    }

    return resolvedValue;
  };

  static getTemplateByTypeId = (typeId: string): Template => {
    return typeTemplateIndex[typeId] || UnknownTypeTemplateJson;
  };

  static formatVariableValue = (value?: any, variable?: UaVariable, symbol?: string): string => {
    const resolvedValue = this.resolveVariableValue(value, variable);
    if (resolvedValue === null || resolvedValue === undefined) {
      return DEFAULT_VALUE_UNDEFINED;
    }

    if (variable?.dataType === 'UtcTime') {
      return this.formatUtcTime(resolvedValue);
    }

    if (variable?.dataType === 'Enum') {
      const variableEnum = variable as UaVariableEnum;
      return variableEnum.enumValues[resolvedValue] || String(resolvedValue);
    }

    if (symbol) return `${resolvedValue.toString()} ${symbol}`;
    return resolvedValue.toString();
  };

  static createFilter = (ns: (number | undefined)[], variablePath?: string): PubSubNodeFilterFactory[] => {
    let filter: PubSubNodeFilterFactory[] = [];

    if (!variablePath) return filter;
    if (variablePath === '.') return [FilterCurrentNode()];

    let variablePathParts: string[] = variablePath.split('.');
    _.forEach(variablePathParts, function (variablePathPart) {
      if (variablePathPart.includes(':')) {
        let variablePathPartSplit: string[] = variablePathPart.split(':');
        let nsIndex = Number(variablePathPartSplit[0]);
        let variableName = variablePathPartSplit[1];
        filter.push(FilterBrowsename({ Name: variableName, NamespaceIndex: ns[nsIndex] }));
      } else {
        filter.push(FilterBrowsename({ Name: variablePathPart }));
      }
    });

    return filter;
  };

  static createReferenceAndTypeFilter = (
    ns: (number | undefined)[],
    reference?: string,
    type?: string,
  ): PubSubNodeFilterFactory[] => {
    let filter: PubSubNodeFilterFactory[] = [];

    if (!reference || !type) return filter;

    let referenceId: NodeId;
    if (reference.includes(':')) {
      let referenceSplit: string[] = reference.split(':');
      let nsIndex = Number(referenceSplit[0]);
      referenceId = { Id: referenceSplit[1], Namespace: ns[nsIndex] };
    } else {
      referenceId = { Id: reference };
    }

    let typeId: NodeId;
    if (type.includes(':')) {
      let typeSplit: string[] = type.split(':');
      let nsIndex = Number(typeSplit[0]);
      typeId = { Id: typeSplit[1], Namespace: ns[nsIndex] };
    } else {
      typeId = { Id: type };
    }

    filter.push(FilterReferenceAndTypeId(referenceId, typeId));

    return filter;
  };

  static getTimelineColorMap = (timeline?: Timeline): { [key: string]: string } => {
    const colors: { [key: string]: string } = {};

    if (!timeline) {
      return colors;
    }

    for (const value in timeline.colorValues) {
      colors[value] = timeline.colorValues[value];
    }

    return colors;
  };

  static getListDisplayValues = (value: any, variable?: UaVariable): string[] => {
    const resolvedValue = this.resolveVariableValue(value, variable);
    if (resolvedValue === null || resolvedValue === undefined) {
      return [];
    }

    if (Array.isArray(resolvedValue)) {
      return resolvedValue.map((entry) => String(entry));
    }

    return [String(resolvedValue)];
  };

  static getChartAmount = (value: any): number => {
    if (!value) return 0;
    const valueNumber = Number(value);
    return isNaN(valueNumber) ? 0 : valueNumber;
  };

  static getBadgeBackgroundColor = (badgeElement: SectionRowElementBadge, variableValue?: any): string => {
    const resolvedValue = this.resolveVariableValue(variableValue, badgeElement.valueVariable);
    if (resolvedValue === null || resolvedValue === undefined) {
      return DEFAULT_BADGE_BACKGROUND_COLOR;
    }
    for (const badgeValue of badgeElement.badgeValues) {
      if (badgeValue.value === String(resolvedValue)) {
        return badgeValue.backgroundColor;
      }
    }
    return DEFAULT_BADGE_BACKGROUND_COLOR;
  };

  static getBadgeTextColor = (badgeElement: SectionRowElementBadge, variableValue?: any): string => {
    const resolvedValue = this.resolveVariableValue(variableValue, badgeElement.valueVariable);
    if (resolvedValue === null || resolvedValue === undefined) {
      return DEFAULT_BADGE_TEXT_COLOR;
    }
    for (const badgeValue of badgeElement.badgeValues) {
      if (badgeValue.value === String(resolvedValue)) {
        return badgeValue.textColor;
      }
    }
    return DEFAULT_BADGE_TEXT_COLOR;
  };

  static getBadgeDisplayValue = (badgeElement: SectionRowElementBadge, variableValue?: any): string => {
    const resolvedValue = this.resolveVariableValue(variableValue, badgeElement.valueVariable);
    if (resolvedValue === null || resolvedValue === undefined) {
      return DEFAULT_VALUE_UNDEFINED;
    }
    for (const badgeValue of badgeElement.badgeValues) {
      if (badgeValue.value === String(resolvedValue)) {
        return badgeValue.label || String(resolvedValue);
      }
    }
    return String(resolvedValue);
  };

  static formatUtcTime = (utcTimeValue: string): string => {
    if (!utcTimeValue) {
      return DEFAULT_VALUE_UNDEFINED;
    }

    // Identify the parts of the date value
    // Example of OPC UAs UtcTime: 2022-4-1T9:53:31:11Z
    // Note: new Date(utcTimeValue) does not work because of missing leading zeros
    const dateAndTimeParts = utcTimeValue.split('T');
    if (dateAndTimeParts.length !== 2) {
      return utcTimeValue;
    }

    const dateParts = dateAndTimeParts[0].split('-');
    if (dateParts.length !== 3) {
      return utcTimeValue;
    }
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // zero-based index for months
    const day = parseInt(dateParts[2]);

    const timeParts = dateAndTimeParts[1].replace('Z', '').split(':');
    if (timeParts.length < 3) {
      return utcTimeValue;
    }
    const hour = parseInt(timeParts[0]);
    const minute = parseInt(timeParts[1]);
    const second = parseInt(timeParts[2]);

    return new Date(year, month, day, hour, minute, second).toLocaleString();
  };

  static getKey = (id: string, text: string, index?: number): string => {
    return `${id}_${text}_${index}`;
  };

  static getPlaceholderKey = (id: string, index: number): string => {
    return this.getKey(id, 'P', index);
  };

  static getPlaceholderObjectKey = (id: string, index: number): string => {
    return this.getKey(id, 'PO', index);
  };
}
